import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApplicationPageValuePayloadModel } from '../../../core/helpers';

import { PendingProviderSelection } from '../../../core/services';
import { ApiService } from '../../../core/services/api.service';

@Injectable({
    providedIn: 'root'
})
export class ApplicationService {
    constructor(private apiService: ApiService) {}

    /**
     * Fetches the user's current application, creating one if they do not have it yet, with any
     * staged provider selection already attached.
     *
     * A POST because the call creates a row when none exists, and because the selection travels
     * with it — the portal used to fetch and then send a separate update, which left the choice
     * in the browser until a particular page rendered (LA-22939).
     */
    getCurrentApplication(
        userId: number,
        selection?: PendingProviderSelection
    ): Observable<any> {
        return this.apiService.post('/applications/current', {
            userId,
            selectedProgramIds: selection?.programIds,
            selectedProviderIds: selection?.providerIds,
            selectedProviderSiteIds: selection?.providerSiteIds,
            portalApplicationSource: selection?.source
        });
    }

    patchCurrentApplication(application: any): Observable<any> {
        return this.apiService.patch(
            `/applications/${application.applicationId}`,
            application
        );
    }

    getSelectedProviderContacts(applicationId: number): Observable<any> {
        return this.apiService.get(
            `/applications/${applicationId}/selectedprovidercontacts`
        );
    }

    getSelectedProviderPrograms(applicationId: number): Observable<any> {
        return this.apiService.get(
            `/applications/${applicationId}/selectedproviderprograms`
        );
    }

    getProviderSiteOptions(applicationId: number): Observable<any> {
        return this.apiService.get(
            `/applications/${applicationId}/providersiteoptions`
        );
    }

    getPersonalInfo(applicationId: number): Observable<any> {
        return this.apiService.get(
            `/applications/${applicationId}/personalinfo`
        );
    }

    savePersonalInfo(personalInfo: any): Observable<any> {
        return this.apiService.put(
            `/applications/${personalInfo.applicationId}/personalinfo`,
            personalInfo
        );
    }

    getBackground(applicationId: number): Observable<any> {
        return this.apiService.get(`/applications/${applicationId}/background`);
    }

    saveBackground(personalInfo: any): Observable<any> {
        return this.apiService.put(
            `/applications/${personalInfo.applicationId}/background`,
            personalInfo
        );
    }

    getEducation(applicationId: number): Observable<any> {
        return this.apiService.get(`/applications/${applicationId}/education`);
    }

    saveEducation(education: any): Observable<any> {
        return this.apiService.put(
            `/applications/${education.applicationId}/education`,
            education
        );
    }

    getDisclosure(applicationId: number): Observable<any> {
        return this.apiService.get(`/applications/${applicationId}/disclosure`);
    }

    saveDisclosure(disclosure: any): Observable<any> {
        return this.apiService.put(
            `/applications/${disclosure.applicationId}/disclosure`,
            disclosure
        );
    }

    getDynamicFormValues(
        applicationId: number,
        applicationPage: number
    ): Observable<any> {
        return this.apiService.get(
            `/applications/${applicationId}/pages/${applicationPage}`
        );
    }

    saveDynamicFormValues(
        applicationId: number,
        applicationPage: number,
        payload: ApplicationPageValuePayloadModel
    ): Observable<any> {
        return this.apiService.put(
            `/applications/${applicationId}/pages/${applicationPage}`,
            payload
        );
    }
}
